import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { Table, Select, Checkbox, Tooltip } from "antd";
import ApprovalSignatureCell from "@components/ui/ApprovalSignatureCell";
import type { ColumnsType } from "antd/es/table";
import { DownOutlined, UpOutlined, DownloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import styles from "./VendorInvoice.module.css";
import { VendorInvoice as VendorInvoiceType, MeterInvoiceData } from "types/invoices";
import useVendorInvoice from "./useVendorInvoice";
import MiscChargeBlade from "@components/blades/misc-charge-blade/MiscChargeBlade";
import { toTestAttrValue, withTestColumnAttrs, type TestAttrs } from "@utils/testAttr";

interface VendorInvoiceProps {
  vendor: VendorInvoiceType;
  monthYear: string;
  onEdit?: (vendorId: number, field: string, value: unknown) => void;
  isVendorApprovalPersisted?: (vendorId: number) => boolean;
  username?: string;
  isApprover?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (vendorId: number, checked: boolean) => void;
  bulkApproved?: boolean;
  onBulkApprovedRemove?: (vendorId: number) => void;
}

type CombinedStatus = 'in_progress' | 'ready_for_approval' | 'not_approved' | 'approved' | 'sent';

export const getCombinedApprovalAndInvoiceStatus = (
  invoiceStatus: string | null | undefined,
  approvalStatus: string | null | undefined,
  invoiceApproved: boolean | null | undefined,
): CombinedStatus => {
  if (invoiceStatus === 'sent') return 'sent';
  if (approvalStatus === 'Approved' || invoiceApproved) return 'approved';
  if (approvalStatus === 'Not Approved') return 'not_approved';
  if (approvalStatus === 'Ready') return 'ready_for_approval';
  return 'in_progress';
};

const VendorInvoice = ({ vendor, monthYear, onEdit, isVendorApprovalPersisted, username, isApprover = false, isSelected = false, onToggleSelect, bulkApproved = false, onBulkApprovedRemove }: VendorInvoiceProps) => {
  const {
    expanded,
    setExpanded,
    hstRate,
    fmtCurrency,
    handleDownloadPDF,
    handleDownloadVendorPDF,
    handleDownloadNspPDF,
  } = useVendorInvoice({ vendor, monthYear });
  const [miscBladeOpen, setMiscBladeOpen] = useState(false);

  const isCounterpartyInvoice = Boolean(vendor.is_counterparty_invoice);

  // The backend (InvoiceComputed, via services/invoiceService.js updateInvoiceTotals) is the
  // source of truth for these totals — net_subtotal_cost already has Admin Fee/Forecast Fee/
  // CIB share netted in server-side. Do NOT recompute them here; the per-meter fee-adjustment
  // rows shown below are informational breakdowns of what's already baked into these numbers.
  const netSubtotalWithFees = vendor.net_subtotal_cost ?? 0;
  const totalHstWithFees = vendor.hst_amount ?? 0;

  const handleFieldChange = (field: string, value: unknown) => {
    if (onEdit && vendor.vendor_id) {
      onEdit(vendor.vendor_id, field, value);
    }
  };


  const handleCombinedStatusChange = (value: CombinedStatus) => {
    switch (value) {
      case 'sent':
        handleFieldChange('invoice_status', 'sent');
        handleFieldChange('approval_status', 'Approved');
        break;
      case 'approved':
        handleFieldChange('invoice_status', 'not');
        handleFieldChange('approval_status', 'Approved');
        break;
      case 'not_approved':
        handleFieldChange('invoice_status', 'not');
        handleFieldChange('approval_status', 'Not Approved');
        break;
      case 'ready_for_approval':
        handleFieldChange('invoice_status', 'not');
        handleFieldChange('approval_status', 'Ready');
        break;
      default:
        handleFieldChange('invoice_status', 'not');
        handleFieldChange('approval_status', 'In Progress');
        break;
    }
  };

  const originallyApproved = isVendorApprovalPersisted ? isVendorApprovalPersisted(vendor.vendor_id!) : vendor.invoice_approved;
  // The approval checkbox optimistically flips approval_status to 'Approved' as soon as it's
  // checked (so the mutation payload is ready for Save Changes), but the Status column must not
  // show Approved until that change is actually persisted — mirrors ProdStat/Dollar Journal, where
  // the equivalent display field is only updated by the server, never by the checkbox handler.
  const displayApprovalStatus = (isVendorApprovalPersisted && vendor.approval_status === 'Approved' && !originallyApproved)
    ? 'Ready'
    : vendor.approval_status;
  const combinedStatus = getCombinedApprovalAndInvoiceStatus(vendor.invoice_status, displayApprovalStatus, originallyApproved);

  // Build invoice download options based on status
  const getInvoiceOptions = () => {
    switch (combinedStatus) {
      case 'approved':
      case 'sent':
        return [
          { value: 'preview', label: 'Preview Invoice', disabled: true },
          { value: 'nsp', label: 'NSP Invoice' },
          { value: 'vendor', label: 'Vendor Invoice' },
        ];
      default:
        // in_progress, ready_for_approval, not_approved
        return [
          { value: 'preview', label: 'Preview Invoice' },
        ];
    }
  };

  // Get the default displayed label for the invoice selector
  const getInvoicePlaceholder = () => {
    const options = getInvoiceOptions();
    const firstEnabled = options.find(o => !o.disabled);
    return firstEnabled?.label ?? 'Download PDF';
  };

  // Show AGC columns if any meter for this vendor is an AGC Wind Farm
  const hasAgcMeters = vendor.meters.some(
    (m) => m.meter_type?.toLowerCase() === 'agc wind farm'
  );


  const meterColumns: ColumnsType<MeterInvoiceData> = [
    {
      title: 'Name',
      dataIndex: 'location',
      key: 'location',
      width: 180,
      sorter: (a, b) => (a.location ?? '').localeCompare(b.location ?? ''),
    },
    {
      title: 'Meter #',
      dataIndex: 'meter_number',
      key: 'meter_number',
      width: 130,
      render: (value: string | null, record: MeterInvoiceData) => (
        <Link to={`/meterreaddetails/${record.meter_id}`} data-testid="meter-number-link">{value}</Link>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'meter_read_type',
      key: 'meter_read_type',
      width: 120,
      render: (value: string | null) => {
        if (value === 'Actual' || value === 'Consolidated Actual') {
          return 'Read';
        }
        return value;
      },
    },
    {
      title: 'Net Gen kWh',
      dataIndex: 'post_prod_stat_netgen',
      key: 'post_prod_stat_netgen',
      width: 120,
      align: 'right',
      render: (v: number | null) =>
        v == null ? '0' : Math.round(v).toLocaleString(undefined),
    },
    ...(hasAgcMeters ? [{
      title: 'AGC Net Gen kWh',
      dataIndex: 'agc_energy_kwh',
      key: 'agc_energy_kwh',
      width: 130,
      align: 'right' as const,
      render: (v: number | null) =>
        v == null ? '—' : Math.round(v).toLocaleString(undefined),
    }] : []),
    {
      title: 'kWh Adjustments',
      key: 'kwh_adjusted',
      width: 130,
      align: 'right',
      render: (_: unknown, record: MeterInvoiceData) => {
        const val = record.kwh_adjusted ?? 0;
        const comment = record.kwh_adjustment_comments;
        const display = val === 0 ? '—' : Math.round(val).toLocaleString(undefined);
        if (comment) {
          return (
            <Tooltip title={comment}>
              <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }} data-tooltip={comment}>{display}</span>
            </Tooltip>
          );
        }
        return display;
      },
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 100,
      align: 'right',
      render: (v: number | null) => v?.toFixed(4) ?? '—',
    },
    {
      title: 'Calculated Cost',
      dataIndex: 'prod_stat_cost_original',
      key: 'prod_stat_cost_original',
      width: 130,
      align: 'right',
      render: fmtCurrency,
    },
    ...(hasAgcMeters ? [{
      title: 'AGC Cost',
      key: 'agc_cost',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: MeterInvoiceData) => {
        if (record.agc_energy_kwh == null || record.rate == null) return '—';
        return fmtCurrency(record.agc_energy_kwh * record.rate);
      },
    }] : []),
    {
      title: 'Dollar Adjustments',
      key: 'adjustments',
      width: 150,
      align: 'right',
      render: (_: unknown, record: MeterInvoiceData) => {
        const val = record.adjustments ?? 0;
        const comments = [record.dollar_adjustment_comments, record.reason_for_adjustment].filter(Boolean).join('\n');
        const display = fmtCurrency(val);
        if (comments) {
          return (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{comments}</span>}>
              <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }} data-tooltip={comments}>{display}</span>
            </Tooltip>
          );
        }
        return display;
      },
    },

    {
      title: 'Final Cost',
      dataIndex: 'dollar_value',
      key: 'final_cost',
      width: 130,
      align: 'right',
      // dollar_value is the backend source of truth and already includes AGC cost
      // (recomputeInvoiceDerivedValues folds agc_cost in) — do not re-add it here.
      render: (v: number | null) => fmtCurrency(v ?? 0),
    },
  ];

  // Render the Approved cell based on status
  const renderApprovedCell = () => {
    const canEdit = combinedStatus === 'ready_for_approval' && isApprover;
    const isReadOnly = combinedStatus === 'approved' || combinedStatus === 'sent';
    const effectiveApproved = bulkApproved || vendor.invoice_approved || false;
    return (
      <ApprovalSignatureCell
        approved={effectiveApproved}
        signature={vendor.invoice_computed_approval_signature}
        canEdit={canEdit}
        username={username}
        onApprovedChange={(checked) => {
          handleFieldChange('invoice_approved', checked);
          handleFieldChange('approval_status', checked ? 'Approved' : 'Ready');
          if (checked && !vendor.invoice_computed_approval_signature && username) {
            handleFieldChange('invoice_computed_approval_signature', username);
          }
          if (!checked) {
            handleFieldChange('invoice_computed_approval_signature', null);
            if (vendor.vendor_id != null) onBulkApprovedRemove?.(vendor.vendor_id);
          }
        }}
        onSignatureChange={(val) => handleFieldChange('invoice_computed_approval_signature', val)}
        readOnlyOverride={isReadOnly}
      />
    );
  };

  const isApproved = combinedStatus === 'approved' || combinedStatus === 'sent';

  return (
    <>
      <tr
        className={styles.vendorRow}
        data-testid="vendor-row"
        data-vendor-id={vendor.vendor_id ?? ''}
        data-vendor-name={vendor.vendor_name}
        data-status={toTestAttrValue(combinedStatus)}
        data-counterparty={String(isCounterpartyInvoice)}
        data-selected={String(isSelected)}
        data-expanded={String(expanded)}
      >
        {/* Select checkbox — only enabled for approved vendors */}
        <td className={styles.checkboxCell}>
          <Checkbox
            checked={isSelected}
            disabled={!isApproved}
            onChange={(e) => {
              if (vendor.vendor_id != null && onToggleSelect) {
                onToggleSelect(vendor.vendor_id, e.target.checked);
              }
            }}
            data-testid="vendor-select-checkbox"
          />
        </td>

        {/* Expand button */}
        <td className={styles.expandCell}>
          <button
            type="button"
            className={styles.expandButton}
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} vendor invoice details for ${vendor.vendor_name}`}
            data-testid="expand-button"
          >
            {expanded ? <UpOutlined className={styles.expandIcon} /> : <DownOutlined className={styles.expandIcon} />}
          </button>
        </td>

        {/* Vendor name + sites */}
        <td className={styles.vendorCell}>
          <div className={styles.vendorName} data-testid="vendor-name">
            {vendor.vendor_name}
            {vendor.misc_charge && (
              <Tooltip title={`Misc charge: ${vendor.misc_charge.reason}`}>
                <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 6, fontSize: 14 }} data-testid="misc-charge-indicator" data-tooltip={`Misc charge: ${vendor.misc_charge.reason}`} />
              </Tooltip>
            )}
          </div>
          <div className={styles.vendorMeta} data-testid="vendor-meta">
            {isCounterpartyInvoice
              ? `Secondary invoice — Co-Vendor for ${vendor.primary_vendor_name ?? 'Primary Vendor'}`
              : `${vendor.meter_count} Sites`}
          </div>
        </td>


        {/* Subtotal (Before Tax) */}
        <td className={styles.dataCell} data-testid="vendor-value" data-column="subtotal">
          {fmtCurrency(netSubtotalWithFees)}
        </td>

        {/* Tax */}
        <td className={styles.dataCell} data-testid="vendor-value" data-column="tax">
          {totalHstWithFees !== 0 ? fmtCurrency(totalHstWithFees) : '—'}
        </td>

        {/* Total Invoice (Incl. Tax) */}
        <td className={styles.dataCell} data-testid="vendor-value" data-column="total">
          {fmtCurrency(netSubtotalWithFees + totalHstWithFees)}
        </td>

        {/* Status */}
        <td className={styles.dataCell}>
          <Select
            value={combinedStatus}
            onChange={handleCombinedStatusChange}
            style={{ width: 210 }}
            size="small"
            className={isApproved ? styles.approvedStatusSelect : undefined}
            disabled={combinedStatus === 'approved' || combinedStatus === 'sent'}
            styles={{ popup: { root: { borderRadius: 20, padding: 8 } } }}
            options={[
              { value: 'in_progress', label: 'In Progress' },
              { value: 'ready_for_approval', label: 'Ready for Approval' },
              ...(isApprover ? [{ value: 'not_approved', label: 'Not Approved - Update Needed' }] : []),
              { value: 'approved', label: 'Approved', disabled: true },
              { value: 'sent', label: 'Invoice Sent', disabled: true },
            ].map((o) => ({ ...o, 'data-testid': 'invoice-status-option', 'data-value': toTestAttrValue(o.value) }))}
            data-testid="invoice-status-select"
          />
        </td>

        {/* Approved */}
        <td className={styles.dataCell}>
          {renderApprovedCell()}
        </td>

        {/* Invoices */}
        <td className={styles.dataCell}>
          <Select
            placeholder={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DownloadOutlined style={{ fontSize: 13 }} />
                {getInvoicePlaceholder()}
              </span>
            }
            style={{ width: 195 }}
            size="small"
            value={null}
            className={styles.invoiceSelect}
            styles={{ popup: { root: { borderRadius: 20, padding: 8 } } }}
            suffixIcon={<DownOutlined style={{ fontSize: 10 }} />}
            onChange={(value) => {
              if (value === 'preview') handleDownloadPDF();
              else if (value === 'vendor') handleDownloadVendorPDF();
              else if (value === 'nsp') handleDownloadNspPDF();
            }}
            options={getInvoiceOptions().map((o) => ({ ...o, 'data-testid': 'invoice-download-option', 'data-value': o.value }))}
            data-testid="invoice-download-select"
          />
        </td>
      </tr>

      {expanded && (
        <tr className={styles.expandedRow} data-testid="vendor-details" data-vendor-id={vendor.vendor_id ?? ''}>
          <td colSpan={9} className={styles.expandedCell}>
            <div className={styles.tableWrapper}>
              <Table<MeterInvoiceData>
                dataSource={vendor.meters}
                columns={withTestColumnAttrs(meterColumns, 'meter-value')}
                rowKey="meter_computed_id"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                data-testid="vendor-meters-table"
                onRow={(meter) => ({
                  'data-testid': 'meter-row',
                  'data-meter-id': String(meter.meter_id),
                  'data-meter-number': meter.meter_number ?? '',
                  'data-meter-name': meter.location ?? '',
                }) as TestAttrs}
              />

              <div className={styles.invoiceSummary} data-testid="invoice-summary">
                <h4 className={styles.summaryTitle}>Invoice Amount</h4>

                <div className={styles.meterCosts}>
                  {vendor.meters.map((meter) => {
                    // Primary invoice: CIB/Counterparty share, Forecast Fee, Admin Fee (in that
                    // order). Counterparty invoice: only the inverse Primary-Vendor-share line.
                    const adjustmentLines = isCounterpartyInvoice
                      ? [
                        meter.primary_vendor_share_amount != null && meter.primary_vendor_share_label
                          ? { key: 'primary_vendor_share', label: meter.primary_vendor_share_label, amount: meter.primary_vendor_share_amount }
                          : null,
                        meter.prior_period_adjustment_amount != null && meter.prior_period_adjustment_amount !== 0
                          && meter.prior_period_adjustment_label
                          ? { key: 'prior_period_adjustment', label: meter.prior_period_adjustment_label, amount: meter.prior_period_adjustment_amount }
                          : null,
                      ]
                      : [
                        meter.counter_party_amount != null && meter.counter_party_label
                          ? { key: 'counter_party', label: meter.counter_party_label, amount: meter.counter_party_amount }
                          : null,
                        meter.forecast_fee_amount != null && meter.forecast_fee_amount !== 0 && meter.forecast_fee_label
                          ? { key: 'forecast_fee', label: meter.forecast_fee_label, amount: meter.forecast_fee_amount }
                          : null,
                        meter.admin_fee_amount != null && meter.admin_fee_amount !== 0
                          && meter.admin_fee_rate != null && meter.admin_fee_rate !== 0 && meter.admin_fee_label
                          ? { key: 'admin_fee', label: meter.admin_fee_label, amount: meter.admin_fee_amount }
                          : null,
                      ];
                    const visibleAdjustmentLines = adjustmentLines.filter(
                      (line): line is { key: string; label: string; amount: number } => line !== null
                    );

                    return (
                      <React.Fragment key={meter.meter_computed_id}>
                        <div className={styles.summaryRow} data-testid="summary-line" data-line="net-cost" data-meter-number={meter.meter_number ?? ''}>
                          <span className={styles.summaryLabel}>
                            Net Cost - {meter.location || meter.meter_number}
                            {meter.adjustment_negative_generation != null && (
                              <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 6 }}>
                              </span>
                            )}
                          </span>
                          <span className={styles.summaryValue} data-testid="summary-value">
                            {fmtCurrency(meter.dollar_value ?? 0)}
                          </span>
                        </div>
                        {visibleAdjustmentLines.map((line) => (
                          <div key={line.key} className={styles.summaryRow} data-testid="summary-line" data-line={toTestAttrValue(line.key)} data-meter-number={meter.meter_number ?? ''}>
                            <span className={styles.summaryLabel}>{line.label}</span>
                            <span className={styles.summaryValue} data-testid="summary-value">{fmtCurrency(line.amount)}</span>
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })}

                </div>

                {(() => {
                  const miscAmount = vendor.misc_charge?.amount ?? 0;
                  const miscHstApplicable = vendor.misc_charge?.hst_applicable ?? false;
                  const miscHst = miscHstApplicable ? miscAmount * hstRate / 100 : 0;
                  const meterSubtotal = netSubtotalWithFees;
                  const subtotal = meterSubtotal + (miscHstApplicable ? miscAmount : 0);
                  const hstTotal = totalHstWithFees + miscHst;
                  const grandTotal = meterSubtotal + miscAmount + hstTotal;

                  const miscRow = (
                    <div className={styles.summaryRow} data-testid="summary-line" data-line="misc-charge">
                      <span className={styles.summaryLabel}>
                        {vendor.misc_charge ? (
                          <>
                            Miscellaneous Charge: {vendor.misc_charge.reason} — HST Applicable: {vendor.misc_charge.hst_applicable ? 'Yes' : 'No'}{' '}
                            {!isApproved && (
                              <button
                                type="button"
                                onClick={() => setMiscBladeOpen(true)}
                                data-testid="misc-charge-button"
                                data-action="edit"
                                style={{ background: 'none', border: 'none', padding: 0, color: '#003366', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
                              >
                                Edit
                              </button>
                            )}
                          </>
                        ) : !isApproved ? (
                          <button
                            type="button"
                            onClick={() => setMiscBladeOpen(true)}
                            data-testid="misc-charge-button"
                            data-action="add"
                            style={{ background: 'none', border: 'none', padding: 0, color: '#003366', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
                          >
                            Add Miscellaneous Charge to this invoice
                          </button>
                        ) : null}
                      </span>
                      {vendor.misc_charge && (
                        <span className={styles.summaryValue} data-testid="summary-value">
                          {fmtCurrency(miscAmount)}
                        </span>
                      )}
                    </div>
                  );

                  return (
                    <>
                      {miscHstApplicable && miscRow}

                      <div className={`${styles.summaryRow} ${styles.subtotalRow}`} data-testid="summary-line" data-line="subtotal">
                        <span className={styles.summaryLabel}>
                          Net Subtotal Cost (Sum of {vendor.meter_count} meter{vendor.meter_count === 1 ? '' : 's'})
                        </span>
                        <span className={styles.summaryValue} data-testid="summary-value">
                          {fmtCurrency(subtotal)}
                        </span>
                      </div>

                      {(hstRate > 0 || isCounterpartyInvoice) && (
                        <div className={styles.summaryRow} data-testid="summary-line" data-line="hst">
                          <span className={styles.summaryLabel}>
                            HST ({isCounterpartyInvoice ? 0 : hstRate}%)
                          </span>
                          <span className={styles.summaryValue} data-testid="summary-value">
                            {fmtCurrency(hstTotal)}
                          </span>
                        </div>
                      )}

                      {!miscHstApplicable && miscRow}

                      <div className={`${styles.summaryRow} ${styles.grandTotalRow}`} data-testid="summary-line" data-line="grand-total">
                        <span className={styles.summaryLabel}>
                          Grand Total of Invoice
                        </span>
                        <span className={styles.summaryValue} data-testid="summary-value">
                          {fmtCurrency(grandTotal)}
                        </span>
                      </div>
                    </>
                  );
                })()}


              </div>
            </div>
          </td>
        </tr>
      )}

      {vendor.vendor_id && ReactDOM.createPortal(
        <MiscChargeBlade
          open={miscBladeOpen}
          onClose={() => setMiscBladeOpen(false)}
          vendorName={vendor.vendor_name}
          vendorId={vendor.vendor_id}
          invoiceMonth={monthYear}
          existingCharge={vendor.misc_charge}
          isApproved={isApproved}
        />,
        document.body
      )}
    </>
  );
};

export default VendorInvoice;
